import {
  Avatar,
  Body,
  Icon,
  UserWithCompanyAvatar,
} from '@assembly-js/design-system';
import classNames from 'classnames';
import React, { FC, memo, PropsWithChildren } from 'react';

import { useNow } from 'src/hooks/useNow';
import { BlackHeadings } from 'src/theme/colors';
import { getTimeFromNow } from 'src/utils/DateUtils';
import { getInitials } from 'src/utils/UserUtils';

import { AvatarGroup } from './AvatarGroup';
import { TruncatedText } from './TruncatedText';

export type ChannelMemberData = {
  name: string;
  avatar: string;
  fallbackColor: string;
  subAvatarInfo?: ChannelMemberData;
};

interface ChannelListItemProps {
  title: string;
  subTitle: string;
  unread: boolean;
  onSelect: React.MouseEventHandler<HTMLDivElement>;
  members?: ChannelMemberData[];
  avatarVariant?: 'circle' | 'rounded';
  selected?: boolean;
  timestamp?: Date;
  // subTitleRenderer is a component that will be used to render the subtitle
  // text of the channel item. This is useful when we want to render the subtitle
  // along side some other component. For example, in the Inbox page we want to
  // show an avatar before the text. The allows the style of the text always be the same
  // in look and feel and the parent component can decide how to render anything
  // along side the text.
  subTitleRenderer?: React.FC<PropsWithChildren>;
  isNotification?: boolean;
}

const ChannelTimeStamp: FC<{ time: Date; unReadNotification: boolean }> = memo(
  ({ time, unReadNotification }) => {
    const { now: nowTime } = useNow();
    const humanizedTimeSince = getTimeFromNow(time, nowTime);

    return (
      <Body
        size="sm"
        className={`text-gray-small text-[13px] leading-5 ${
          unReadNotification ? 'font-medium' : 'font-normal'
        }`}
      >
        {humanizedTimeSince}
      </Body>
    );
  },
);

ChannelTimeStamp.displayName = 'ChannelTimeStamp';

export const DefaultSubTitleRenderer: FC<PropsWithChildren> = ({
  children,
}) => <>{children}</>;

export const ChannelListItem: FC<ChannelListItemProps> = ({
  members,
  title,
  subTitle,
  subTitleRenderer,
  unread,
  timestamp,
  onSelect,
  selected,
  isNotification = false,
  avatarVariant,
}) => {
  const [member] = members || [];

  const SubTitleRenderer = subTitleRenderer ?? DefaultSubTitleRenderer;
  const isCompanyMembership = avatarVariant === 'rounded';

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(event as unknown as React.MouseEvent<HTMLDivElement>);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className={classNames(
        'w-full h-[68px] px-[20px] py-[12px] flex items-center justify-between border-b border-solid border-t-0 border-x-0 border-b-[#DFE1E4] sm:hover:bg-[#F8F9FB] sm:hover:cursor-pointer box-border',
        {
          'sm:bg-[#F8F9FB]': selected,
        },
      )}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
    >
      <div
        className={classNames('flex items-center flex-1 min-w-0', {
          'gap-3': !isNotification,
          'gap-0': isNotification,
        })}
      >
        {members &&
          (members.length > 1 ? (
            <AvatarGroup>
              {members
                .slice(0, 2)
                .map(({ avatar: src, fallbackColor, name }) => (
                  <Avatar
                    key={`${name}-${src}`}
                    size="md"
                    src={src}
                    variant="circle"
                    fallbackColor={fallbackColor ?? ''}
                    text={getInitials(name, { twoLetters: true }) || ''}
                  />
                ))}
            </AvatarGroup>
          ) : isCompanyMembership ? (
            <Avatar
              size="md"
              src={member?.avatar || ''}
              variant="rounded"
              fallbackColor={member?.fallbackColor ?? ''}
              text={getInitials(member?.name)}
            />
          ) : (
            <UserWithCompanyAvatar
              size="md"
              avatarSrc={member?.avatar || ''}
              fallbackColor={member?.fallbackColor ?? ''}
              text={getInitials(member?.name, { twoLetters: true }) || ''}
              companyAvatarSrc={member?.subAvatarInfo?.avatar || ''}
              companyAvatarFallbackColor={
                member?.subAvatarInfo?.fallbackColor ?? ''
              }
              companyText={getInitials(member?.subAvatarInfo?.name) || ''}
            />
          ))}

        <div className="flex flex-col justify-center flex-1 min-w-0 overflow-hidden">
          <TruncatedText
            text={title}
            styleProps={{
              fontSize: '13px',
              lineHeight: '20px',
              color: BlackHeadings,
              fontWeight: unread ? 500 : 400,
              letterSpacing: '-0.05px', // this matches spacing with legacy MUI
            }}
          />
          <SubTitleRenderer>
            {subTitle && (
              <Body
                size="sm"
                className="text-gray-small leading-[21px] pr-3 text-ellipsis overflow-hidden whitespace-nowrap"
              >
                {subTitle}
              </Body>
            )}
          </SubTitleRenderer>
        </div>
      </div>

      <div
        className={classNames(
          'h-full box-content flex flex-col items-end flex-shrink-0',
          {
            'justify-between': unread,
            'justify-end': !unread,
          },
        )}
      >
        <div className="h-full flex items-center">
          {unread && (
            <Icon
              icon="Dot"
              style={{
                color: BlackHeadings,
              }}
              height={8}
              width={8}
            />
          )}
        </div>
        {timestamp && (
          <ChannelTimeStamp time={timestamp} unReadNotification={unread} />
        )}
      </div>
    </div>
  );
};
